import { useState } from "react";
import { track } from "@/lib/analytics";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db2jjrom252sfkho245g";

const HELP_OPTIONS = [
  "Answering customer questions",
  "Capturing more leads",
  "Booking more appointments",
  "All of the above",
];

type Status = "idle" | "submitting" | "success" | "error";

/**
 * Shared lead-capture form (email + what they need help with).
 * Used on the home page.
 */
export default function LeadForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      // Silently treat bots as a normal success.
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const lowerMsg = String(serverMsg).toLowerCase();

      const isSpam = lowerMsg.includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (!isOk) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim().length > 0
            ? serverMsg
            : "We couldn't submit your details just now. Please try again."
        );
        return;
      }

      setStatus("success");
      track("lead_form_submitted", { location: "get_started_form" });
      form.reset();
    } catch (error) {
      setStatus("error");
      setFormError(
        error instanceof Error
          ? error.message
          : "Something went wrong while submitting. Please try again."
      );
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-background-300/40 bg-background-100/80 backdrop-blur p-8 text-center animate-fadeIn">
        <div className="w-14 h-14 mx-auto rounded-full bg-primary-500/15 flex items-center justify-center mb-4">
          <i className="ri-check-line text-2xl text-primary-400" aria-hidden></i>
        </div>
        <h3 className="text-lg font-semibold text-foreground-950 mb-2">You&apos;re on the list</h3>
        <p className="text-sm text-foreground-700 leading-relaxed">
          Thanks! We&apos;ve got your email and what you need help with. A Joble specialist will reach
          out shortly, and you can jump straight in any time.
        </p>
        <a
          href="https://app.joble.app/auth/create-account?free=true"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("try_joble_clicked", { location: "lead_form_success" })}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 text-foreground-950 text-sm font-semibold px-5 py-2.5 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          Start your trial today
          <i className="ri-arrow-right-line" aria-hidden></i>
        </a>
      </div>
    );
  }

  return (
    <form
      id="joble-try-now-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-background-300/40 bg-background-100/80 backdrop-blur p-6 md:p-7"
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="joble-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
            Email
          </label>
          <input
            id="joble-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@company.com"
            className="w-full rounded-md bg-background-50/70 border border-background-300/50 text-sm text-foreground-950 placeholder:text-foreground-500 px-3.5 py-2.5 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="joble-help" className="block text-xs font-medium text-foreground-700 mb-1.5">
            What do you need help with?
          </label>
          <select
            id="joble-help"
            name="help"
            defaultValue={HELP_OPTIONS[0]}
            className="w-full rounded-md bg-background-50/70 border border-background-300/50 text-sm text-foreground-800 px-3.5 py-2.5 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30 transition-colors cursor-pointer"
          >
            {HELP_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="joble-extra-contact" aria-hidden="true">
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            placeholder="Leave this field empty"
          />
        </div>
      </div>

      {status === "error" && formError && (
        <p className="mt-4 text-xs text-accent-300 bg-accent-950/40 border border-accent-700/40 rounded-md px-3 py-2">
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 text-foreground-950 text-sm font-semibold px-6 py-3 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" aria-hidden></i>
            Sending…
          </>
        ) : (
          <>
            Get started
            <i className="ri-arrow-right-line" aria-hidden></i>
          </>
        )}
      </button>

      <p className="mt-3 text-center text-xs text-foreground-600">
        By continuing you agree to our Terms and Privacy Policy.
      </p>
    </form>
  );
}